import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

type AnyClient = { from: (t: string) => any; rpc: (n: string, a?: unknown) => any };

/** Alla annonser som den inloggade har sparat – bara slugarna, för hjärtat i listan. */
export const listMyFavoriteSlugs = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const client = context.supabase as unknown as AnyClient;
    const { data } = await client
      .from("ad_favorites")
      .select("ad_id, ads(slug)")
      .eq("user_id", context.userId);
    return ((data ?? []) as any[]).map((r) => r.ads?.slug).filter(Boolean) as string[];
  });

/** Hela listan «Sparade annonser» med aktuellt pris och prisändring sedan du sparade. */
export const listMyFavorites = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const client = context.supabase as unknown as AnyClient;
    const { data } = await client
      .from("ad_favorites")
      .select(
        "created_at, price_at_save, ads(slug, title, price, image_url, image_urls, category, location, created_at, is_paused, sold_at, sale_kind, auction_ends_at, auction_status)",
      )
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false });

    return ((data ?? []) as any[])
      .filter((r) => r.ads)
      .map((r) => ({
        savedAt: r.created_at as string,
        priceAtSave: (r.price_at_save ?? null) as number | null,
        ad: r.ads,
      }));
  });

/** Sätter eller tar bort hjärtat. Returnerar det nya läget. */
export const toggleFavorite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1).max(80) }).parse(input))
  .handler(async ({ data, context }) => {
    const client = context.supabase as unknown as AnyClient;
    const { data: ad } = await client
      .from("ads")
      .select("id, price")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!ad) throw new Error("Annonsen finns inte.");

    const { data: existing } = await client
      .from("ad_favorites")
      .select("id")
      .eq("user_id", context.userId)
      .eq("ad_id", ad.id)
      .maybeSingle();

    if (existing) {
      await client.from("ad_favorites").delete().eq("id", existing.id);
      return { favorited: false };
    }

    const { error } = await client
      .from("ad_favorites")
      .insert({ user_id: context.userId, ad_id: ad.id, price_at_save: ad.price ?? null });
    if (error) throw new Error("Kunde inte spara annonsen.");
    return { favorited: true };
  });

/** Antal sparningar på en egen annons – visas i annonsstatistiken för säljaren. */
export const getFavoriteCount = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1).max(80) }).parse(input))
  .handler(async ({ data, context }) => {
    const client = context.supabase as unknown as AnyClient;
    const { data: ad } = await client.from("ads").select("id").eq("slug", data.slug).maybeSingle();
    if (!ad) return 0;
    const { data: count } = await client.rpc("ad_favorite_count", { _ad_id: ad.id });
    return (count ?? 0) as number;
  });
